/**
 * Compact loudness envelope of an audio track: one RMS value per fixed window.
 * A 1 hour video at 20 ms windows is only 180k floats (~700 KB), so the whole
 * analysis stays cheap no matter how long the source is.
 */
export interface AudioEnvelope {
  /** Window length in seconds. */
  readonly windowSec: number;
  /** Linear RMS (0..1) per window; window `i` covers `[i * windowSec, (i + 1) * windowSec)`. */
  readonly rms: Float32Array;
}

export const SILENT_DB = -100;

export const toDb = (linear: number): number => (linear > 0 ? Math.max(SILENT_DB, 20 * Math.log10(linear)) : SILENT_DB);

/**
 * Streams decoded audio chunks into an {@link AudioEnvelope}. Chunks are placed by
 * their timestamp, so gaps, start offsets and arbitrary chunk sizes are all handled.
 */
export class EnvelopeBuilder {
  private sums = new Float64Array(1024);
  private counts = new Uint32Array(1024);
  private length = 0;

  constructor(readonly windowSec = 0.02) {
    if (!(windowSec > 0)) throw new RangeError('windowSec must be positive');
  }

  /** Adds mono samples starting at `timestampSec`. Negative timestamps (pre-roll) are ignored. */
  push(timestampSec: number, samples: Float32Array, sampleRate: number): void {
    if (!(sampleRate > 0) || samples.length === 0) return;
    const samplesPerWindow = this.windowSec * sampleRate;
    const startPosition = timestampSec * sampleRate;
    for (let i = 0; i < samples.length; i++) {
      const position = startPosition + i;
      if (position < 0) continue;
      const index = Math.floor(position / samplesPerWindow);
      this.ensureCapacity(index + 1);
      const value = samples[i]!;
      this.sums[index]! += value * value;
      this.counts[index]!++;
    }
  }

  build(): AudioEnvelope {
    const rms = new Float32Array(this.length);
    for (let i = 0; i < this.length; i++) {
      const count = this.counts[i]!;
      rms[i] = count > 0 ? Math.sqrt(this.sums[i]! / count) : 0;
    }
    return { windowSec: this.windowSec, rms };
  }

  private ensureCapacity(size: number): void {
    if (size > this.length) this.length = size;
    if (size <= this.sums.length) return;
    let capacity = this.sums.length;
    while (capacity < size) capacity *= 2;
    const sums = new Float64Array(capacity);
    sums.set(this.sums);
    const counts = new Uint32Array(capacity);
    counts.set(this.counts);
    this.sums = sums;
    this.counts = counts;
  }
}

/** Averages interleaved or planar channels into a mono buffer. */
export function downmixPlanar(channels: readonly Float32Array[]): Float32Array {
  const first = channels[0];
  if (!first) return new Float32Array(0);
  if (channels.length === 1) return first;
  const mono = new Float32Array(first.length);
  for (const channel of channels) {
    for (let i = 0; i < mono.length; i++) mono[i]! += channel[i] ?? 0;
  }
  const scale = 1 / channels.length;
  for (let i = 0; i < mono.length; i++) mono[i]! *= scale;
  return mono;
}

/**
 * Reduces an envelope to `buckets` peak values (0..1) for waveform drawing,
 * over `[fromSec, toSec)` (all of it by default). Peaks (not averages) keep
 * short transients visible at any zoom level; they are scaled by the loudest
 * moment of the whole sound, so a zoomed-in stretch keeps its true height.
 */
export function envelopePeaks(envelope: AudioEnvelope, buckets: number, fromSec = 0, toSec = envelope.rms.length * envelope.windowSec): Float32Array {
  const count = Math.max(0, Math.floor(buckets));
  const peaks = new Float32Array(count);
  const { rms, windowSec } = envelope;
  if (count === 0 || rms.length === 0 || !(toSec > fromSec)) return peaks;
  let max = 0;
  for (let i = 0; i < rms.length; i++) max = Math.max(max, rms[i]!);
  const first = fromSec / windowSec;
  const span = (toSec - fromSec) / windowSec;
  for (let b = 0; b < count; b++) {
    const from = Math.floor(first + (b * span) / count);
    const to = Math.max(from + 1, Math.floor(first + ((b + 1) * span) / count));
    let peak = 0;
    for (let i = Math.max(0, from); i < to && i < rms.length; i++) peak = Math.max(peak, rms[i]!);
    peaks[b] = peak;
  }
  if (max > 0) for (let b = 0; b < count; b++) peaks[b]! /= max;
  return peaks;
}

/**
 * Loudest point of each window `[from, to)` (seconds), 0..1 relative to the
 * loudest point of the whole sound; a null window (nothing plays there) is 0.
 * For drawing a sound that is cut, looped or sped up, where windows aren't evenly spaced.
 */
export function envelopePeaksAt(envelope: AudioEnvelope, windows: readonly (readonly [number, number] | null)[]): Float32Array {
  const peaks = new Float32Array(windows.length);
  const { rms, windowSec } = envelope;
  if (rms.length === 0 || !(windowSec > 0)) return peaks;
  let max = 0;
  for (let i = 0; i < rms.length; i++) max = Math.max(max, rms[i]!);
  if (!(max > 0)) return peaks;
  windows.forEach((window, b) => {
    if (!window) return;
    const from = Math.max(0, Math.floor(window[0] / windowSec));
    const to = Math.min(rms.length, Math.max(from + 1, Math.ceil(window[1] / windowSec)));
    let peak = 0;
    for (let i = from; i < to; i++) peak = Math.max(peak, rms[i]!);
    peaks[b] = peak / max;
  });
  return peaks;
}
